import { Skeleton, toast } from "@credit-acceptance/uikit";
import { CancelledError, useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { type Control, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../../components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "../../../../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../../components/ui/select";
import { Checkbox } from "../../../../../components/ui/uikit/checkbox";
import { CurrencyInput } from "../../../../../components/ui/uikit/currencyInput";
import { DateInput } from "../../../../../components/ui/uikit/dateInput";
import { Input } from "../../../../../components/ui/uikit/input";
import { Textarea } from "../../../../../components/ui/uikit/textarea";
import { computeShouldForceExit } from "../../../../../hooks/useForceExitCheck";
import { formatIsoDate, formatSsn } from "../../../../../lib/format";
import { getErrorMessage, isHttpError } from "../../../../../lib/query/errors";
import { cn } from "../../../../../lib/utils";
import { useForceExitStore } from "../../../../../stores/forceExitStore";
import { formatNoteDatetime } from "../../../../analystNotes/formatNoteDatetime";
import ForceExitWarningModal from "../../../../exit/ForceExitWarningModal";
import { applicationStatusQuery } from "../../../api/useApplicationStatus";
import { useDscWorkbench } from "../../../api/useDscWorkbench";
import { isPreApprovalApp } from "../../../isPreApprovalApp";
import { useSaveAllSections } from "../../../useSaveAllSections";
import { filterHistoryEntriesForEmployment, formatHistoryAnswerValue } from "../api/callScript.mapping";
import type { CallScriptSaveWarning } from "../api/callScript.savePayload";
import { type CallScriptVerifiedFields, EMPTY_CALL_SCRIPT_VERIFIED_FIELDS } from "../api/callScript.types";
import {
  buildSavedFieldsForAnswers,
  mapEmploymentToSavedFields,
  NO_SAVED_COUNTERPART_FIELDS,
} from "../api/callScriptEmploymentMapping";
import { buildQuestionPkMap } from "../api/callScriptQuestions";
import { type CallScriptFieldStates, validateEmploymentVerification } from "../api/callScriptValidation";
import { useCallScriptHistory } from "../api/useCallScriptHistory";
import { ensureCallScriptQuestionsForPerson, prefetchAllCallScriptQuestions } from "../api/useCallScriptQuestions";
import { useSaveCallScript } from "../api/useSaveCallScript";
import { isCompleteZip, useZipLookup } from "../api/useZipLookup";
import { applyDigitFormatWithCaret } from "../caretPreservingDigitFormat";
import {
  INCOME_TYPE_OPTIONS,
  OCCUPATION_INCOME_TYPE_CODES,
  PAY_FREQUENCY_OPTIONS,
  PHONE_NUMBER_DIGITS,
  US_STATE_OPTIONS,
} from "../constants";
import { useFormReadOnly } from "../readOnlyContext";
import type { SignersInput } from "../Signers.schema";
import { formatPhoneDisplay, formatPhoneFromRaw, formatPhoneOrDigits } from "./PhoneNumberInput";
import { useDraggableDialog } from "./useDraggableDialog";
import type { ZipLookupStatus } from "./useZipAutofill";
import { ZipLookupMessage } from "./ZipLookupMessage";

type PersonPath = "driver" | "secondSigner";

interface CallScriptModalProperties {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Deal / approval identifier — path param for both Call Script calls, and displayed in the side panel. */
  approvalId?: string;
  /** Applicant identifier — path param for both Call Script calls. */
  applicantId?: string;
  /** Which person's income this call is for — selects the saved Employment record from the dashboard. */
  personPath: PersonPath;
  /** Index of the income block within that person's incomes. */
  index: number;
  /** RHF control — reads the Driver's name/SSN for the side panel. */
  control: Control<SignersInput>;
}

type RowKind =
  | { kind: "text"; placeholder?: string }
  | { kind: "phone"; placeholder?: string }
  | { kind: "number" }
  | { kind: "currency" }
  | { kind: "date" }
  | { kind: "checkbox" }
  | { kind: "yesNo" }
  | { kind: "select"; options: ReadonlyArray<{ value: string; label: string }> };

type VerifiedRowDefinition = {
  key: keyof CallScriptVerifiedFields;
  label: string;
  /**
   * When set, the row (both columns) only renders while this predicate is
   * true (e.g. Physical Work Location / Occupation fields). Takes `saved`
   * too — Physical Work Location rows need it so a saved work location isn't
   * hidden behind the (always-reset-to-blank) Verified checkbox.
   */
  showWhen?: (verified: CallScriptVerifiedFields, saved: CallScriptVerifiedFields) => boolean;
} & RowKind;

/**
 * Reports whether a uikit DatePicker calendar is currently open.
 *
 * The calendar lives in its own portal at the end of `document.body`, so there is no
 * React-side signal for it; the day grid uikit renders inside the popover is the only
 * marker that tells it apart from any other popover on the page.
 * @returns {boolean} True while a calendar overlay is mounted.
 */
function isCalendarOverlayOpen(): boolean {
  return document.querySelector('[data-slot="popover-content"] [data-slot="calendar"]') !== null;
}

/**
 * Closes an open uikit DatePicker calendar.
 *
 * uikit keeps the popover's open state to itself, and its Radix copy is a different
 * module instance than this Dialog's, so the only handle we have on it is the
 * pointer-down-outside dismissal its own layer listens for. Dispatching that on the
 * body reaches the calendar without touching the Dialog, whose `onInteractOutside`
 * already refuses to dismiss.
 */
function closeCalendarOverlay() {
  document.body.dispatchEvent(new Event("pointerdown", { bubbles: true, cancelable: true }));
}

function buildRows(t: TFunction): VerifiedRowDefinition[] {
  return [
    { key: "employerName", label: t("review.income.callScriptModal.fields.employerName"), kind: "text" },
    {
      key: "phoneNumberDialed",
      label: t("review.income.callScriptModal.fields.phoneNumberDialed"),
      kind: "phone",
      placeholder: "000-000-0000",
    },
    {
      key: "verificationOutsourced",
      label: t("review.income.callScriptModal.fields.verificationOutsourced"),
      kind: "yesNo",
    },
    {
      key: "originalNumberBad",
      label: t("review.income.callScriptModal.fields.originalNumberBad"),
      kind: "yesNo",
    },
    { key: "verifiedWith", label: t("review.income.callScriptModal.fields.verifiedWith"), kind: "text" },
    { key: "hireDate", label: t("review.income.callScriptModal.fields.hireDate"), kind: "date" },
    {
      key: "employmentType",
      label: t("review.income.callScriptModal.fields.employmentType"),
      kind: "select",
      options: INCOME_TYPE_OPTIONS,
    },
    {
      key: "occupation",
      label: t("review.income.callScriptModal.fields.occupation"),
      kind: "text",
      showWhen: (verified) => OCCUPATION_INCOME_TYPE_CODES.has(verified.employmentType),
    },
    { key: "streetNumber", label: t("review.income.callScriptModal.fields.streetNumber"), kind: "text" },
    { key: "streetName", label: t("review.income.callScriptModal.fields.streetName"), kind: "text" },
    { key: "apartmentNumber", label: t("review.income.callScriptModal.fields.apartmentNumber"), kind: "text" },
    {
      key: "zip",
      label: t("review.income.callScriptModal.fields.zip"),
      kind: "text",
      placeholder: "00000",
    },
    { key: "city", label: t("review.income.callScriptModal.fields.city"), kind: "text" },
    {
      key: "state",
      label: t("review.income.callScriptModal.fields.state"),
      kind: "select",
      options: US_STATE_OPTIONS,
    },
    {
      key: "employerPhone",
      label: t("review.income.callScriptModal.fields.employerPhone"),
      kind: "phone",
      placeholder: "000-000-0000",
    },
    {
      key: "differentWorkLocation",
      label: t("review.income.callScriptModal.fields.differentWorkLocation"),
      kind: "checkbox",
    },
    {
      key: "workLocationName",
      label: t("review.income.callScriptModal.fields.workLocationName"),
      kind: "text",
      showWhen: (verified, saved) => verified.differentWorkLocation || saved.differentWorkLocation,
    },
    {
      key: "workLocationStreetNumber",
      label: t("review.income.callScriptModal.fields.workLocationStreetNumber"),
      kind: "text",
      showWhen: (verified, saved) => verified.differentWorkLocation || saved.differentWorkLocation,
    },
    {
      key: "workLocationStreetName",
      label: t("review.income.callScriptModal.fields.workLocationStreetName"),
      kind: "text",
      showWhen: (verified, saved) => verified.differentWorkLocation || saved.differentWorkLocation,
    },
    {
      key: "workLocationApartmentNumber",
      label: t("review.income.callScriptModal.fields.workLocationApartmentNumber"),
      kind: "text",
      showWhen: (verified, saved) => verified.differentWorkLocation || saved.differentWorkLocation,
    },
    {
      key: "workLocationZip",
      label: t("review.income.callScriptModal.fields.workLocationZip"),
      kind: "text",
      placeholder: "00000",
      showWhen: (verified, saved) => verified.differentWorkLocation || saved.differentWorkLocation,
    },
    {
      key: "workLocationCity",
      label: t("review.income.callScriptModal.fields.workLocationCity"),
      kind: "text",
      showWhen: (verified, saved) => verified.differentWorkLocation || saved.differentWorkLocation,
    },
    {
      key: "workLocationState",
      label: t("review.income.callScriptModal.fields.workLocationState"),
      kind: "select",
      options: US_STATE_OPTIONS,
      showWhen: (verified, saved) => verified.differentWorkLocation || saved.differentWorkLocation,
    },
    {
      key: "workLocationPhone",
      label: t("review.income.callScriptModal.fields.workLocationPhone"),
      kind: "phone",
      placeholder: "000-000-0000",
      showWhen: (verified, saved) => verified.differentWorkLocation || saved.differentWorkLocation,
    },
    { key: "dateOfTermination", label: t("review.income.callScriptModal.fields.dateOfTermination"), kind: "date" },
    {
      key: "payFrequency",
      label: t("review.income.callScriptModal.fields.payFrequency"),
      kind: "select",
      options: PAY_FREQUENCY_OPTIONS,
    },
    {
      key: "grossPayPerPeriod",
      label: t("review.income.callScriptModal.fields.grossPayPerPeriod"),
      kind: "currency",
    },
    { key: "avgHoursPerWeek", label: t("review.income.callScriptModal.fields.avgHoursPerWeek"), kind: "number" },
  ];
}

type ChangedFieldRow = {
  key: keyof CallScriptVerifiedFields;
  label: string;
  verifiedDisplay: string;
  savedDisplay: string;
};

/** Renders a row's raw value the way the save-confirmation dialog's comparison table displays it. */
function formatRowValueForDisplay(
  row: VerifiedRowDefinition,
  value: CallScriptVerifiedFields[keyof CallScriptVerifiedFields],
  noValueLabel: string
): string {
  switch (row.kind) {
    case "checkbox": {
      return value === true ? "Yes" : "No";
    }
    case "yesNo": {
      return value === true ? "Y" : "N";
    }
    case "date": {
      return typeof value === "string" && value !== "" ? formatIsoDate(value) : noValueLabel;
    }
    case "currency": {
      return typeof value === "number"
        ? `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : noValueLabel;
    }
    case "phone": {
      if (typeof value !== "string" || value.trim() === "") {
        return noValueLabel;
      }
      return formatPhoneDisplay(value.replaceAll(/\D/gu, "").slice(0, PHONE_NUMBER_DIGITS));
    }
    case "number": {
      return value === "" || value === undefined ? noValueLabel : String(value);
    }
    case "select": {
      const option = row.options.find((candidate) => candidate.value === value);
      if (option) {
        return option.label;
      }
      return typeof value === "string" && value !== "" ? value : noValueLabel;
    }
    default: {
      return typeof value === "string" && value !== "" ? value : noValueLabel;
    }
  }
}

/**
 * Whether a {@link NO_SAVED_COUNTERPART_FIELDS} value counts as "entered" for the
 * confirm-dialog comparison — yes/no fields default to their unset ("No") state,
 * everything else defaults to blank.
 */
function isRowValueEntered(
  row: VerifiedRowDefinition,
  value: CallScriptVerifiedFields[keyof CallScriptVerifiedFields]
): boolean {
  if (row.kind === "checkbox" || row.kind === "yesNo") {
    return value === true;
  }
  return value !== "" && value !== undefined;
}

/**
 * Whether a single row's Verified value counts as a live "changed" highlight
 * (OR-21767 spec §4 `callVerificationChange`): a field with no saved
 * counterpart is "changed" as soon as anything is entered (there's nothing
 * to compare against); a field with a saved counterpart is "changed" only
 * once its entered value differs from that saved value. Phone rows compare
 * on the normalized digit/format (see {@link formatPhoneOrDigits}) rather
 * than the raw string — the saved side (straight from the Employment
 * record) can come back dashed while Verified is always raw digits, and a
 * plain `!==` would flag re-entering the identical number as "changed".
 * @param {VerifiedRowDefinition} row - The field-row definition.
 * @param {string | boolean | number} verifiedValue - The current "Verified Information" value.
 * @param {string | boolean | number} savedValue - The "Saved Information" value.
 * @returns {boolean} Whether the field should render its live "changed" highlight.
 */
function isRowChanged(
  row: VerifiedRowDefinition,
  verifiedValue: CallScriptVerifiedFields[keyof CallScriptVerifiedFields],
  savedValue: CallScriptVerifiedFields[keyof CallScriptVerifiedFields]
): boolean {
  if (!isRowValueEntered(row, verifiedValue)) {
    return false;
  }
  if (NO_SAVED_COUNTERPART_FIELDS.has(row.key)) {
    return true;
  }
  if (row.kind === "phone") {
    return formatPhoneOrDigits(String(verifiedValue)) !== formatPhoneOrDigits(String(savedValue));
  }
  return verifiedValue !== savedValue;
}

/**
 * Builds the save-confirmation dialog's "changed" rows (OR-21767 follow-up): fields with
 * a saved counterpart whose Verified value differs from Saved, plus fields with no saved
 * counterpart at all — those show whenever the analyst entered something, always paired
 * with the em dash as their Saved value since there's nothing to compare against.
 * @param {VerifiedRowDefinition[]} rows - The full ordered field-row definitions.
 * @param {CallScriptVerifiedFields} verified - The current "Verified Information" form values.
 * @param {CallScriptVerifiedFields} savedFields - The "Saved Information" column values.
 * @param {string} noSavedValue - The placeholder ("—") used for fields with nothing saved.
 * @returns {ChangedFieldRow[]} Only the rows that differ (or, for saveless fields, are entered).
 */
function buildChangedFieldRows(
  rows: VerifiedRowDefinition[],
  verified: CallScriptVerifiedFields,
  savedFields: CallScriptVerifiedFields,
  noSavedValue: string
): ChangedFieldRow[] {
  const changed: ChangedFieldRow[] = [];
  for (const row of rows) {
    const verifiedValue = verified[row.key];
    const savedValue = savedFields[row.key];
    if (!isRowChanged(row, verifiedValue, savedValue)) {
      continue;
    }
    const hasSavedCounterpart = !NO_SAVED_COUNTERPART_FIELDS.has(row.key);
    changed.push({
      key: row.key,
      label: row.label,
      verifiedDisplay: formatRowValueForDisplay(row, verifiedValue, noSavedValue),
      savedDisplay: hasSavedCounterpart ? formatRowValueForDisplay(row, savedValue, noSavedValue) : noSavedValue,
    });
  }
  return changed;
}

/** i18n keys valid as a save-warning group's display heading. */
type WarningLabelKey =
  | "review.income.callScriptModal.warnings.employmentWritebackFailed"
  | "review.income.callScriptModal.warnings.generic";

/** Save-warning `code` -> the i18n key for its display heading. Codes with no entry here fall back to `GENERIC_WARNING_LABEL_KEY`. */
const WARNING_CODE_LABEL_KEYS: Partial<Record<string, WarningLabelKey>> = {
  employment_writeback_failed: "review.income.callScriptModal.warnings.employmentWritebackFailed",
};

const GENERIC_WARNING_LABEL_KEY: WarningLabelKey = "review.income.callScriptModal.warnings.generic";

/** One save-warning group: a display heading (by code) plus that code's `detail` messages, in response order. */
interface WarningGroup {
  code: string;
  details: string[];
}

/**
 * Groups save-response warnings by `code`, preserving each group's and each
 * detail's original order — so multiple warnings sharing a code render under
 * one heading instead of repeating it.
 * @param {CallScriptSaveWarning[]} warnings - The save response's `warnings` array.
 * @returns {WarningGroup[]} One entry per distinct code, in first-seen order.
 */
function groupWarningsByCode(warnings: CallScriptSaveWarning[]): WarningGroup[] {
  const groups: WarningGroup[] = [];
  const groupByCode = new Map<string, WarningGroup>();
  for (const warning of warnings) {
    let group = groupByCode.get(warning.code);
    if (!group) {
      group = { code: warning.code, details: [] };
      groupByCode.set(warning.code, group);
      groups.push(group);
    }
    group.details.push(warning.detail);
  }
  return groups;
}

interface VerifiedControlProperties {
  row: VerifiedRowDefinition;
  id: string;
  value: CallScriptVerifiedFields[keyof CallScriptVerifiedFields];
  onChange: (value: CallScriptVerifiedFields[keyof CallScriptVerifiedFields]) => void;
  disabled: boolean;
  ariaLabel: string;
  /** Submit-time validation state (OR-21767) — red border + aria-invalid, no popup. */
  invalid?: boolean;
  /** Re-validates this field immediately, mirroring the Signers form's RHF `onBlur` mode. Only wired for date fields. */
  onBlur?: () => void;
}

function VerifiedControl({
  row,
  id,
  value,
  onChange,
  disabled,
  ariaLabel,
  invalid,
  onBlur,
}: VerifiedControlProperties) {
  switch (row.kind) {
    case "checkbox": {
      return (
        <Checkbox
          id={id}
          aria-label={ariaLabel}
          checked={Boolean(value)}
          onCheckedChange={(checked) => onChange(checked === true)}
          disabled={disabled}
        />
      );
    }
    case "yesNo": {
      const stringValue = value === true ? "Y" : "N";
      return (
        <Select value={stringValue} onValueChange={(next) => onChange(next === "Y")} disabled={disabled}>
          <SelectTrigger id={id} aria-label={ariaLabel} className="h-5 w-full text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Y">Y</SelectItem>
            <SelectItem value="N">N</SelectItem>
          </SelectContent>
        </Select>
      );
    }
    case "date": {
      return (
        <DateInput
          id={id}
          value={typeof value === "string" ? value : ""}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-invalid={invalid}
          className={cn("h-5 text-xs", invalid && "border-destructive")}
        />
      );
    }
    case "currency": {
      return (
        <CurrencyInput
          id={id}
          aria-label={ariaLabel}
          value={typeof value === "number" ? value : undefined}
          onChange={(next) => onChange(next ?? "")}
          disabled={disabled}
          className="h-5 text-xs"
        />
      );
    }
    case "number": {
      const stringValue = value === "" || value === undefined ? "" : String(value);
      return (
        <Input
          id={id}
          type="text"
          inputMode="decimal"
          value={stringValue}
          onChange={(event) => {
            const digits = event.target.value.replaceAll(/[^\d.]/g, "");
            onChange(digits === "" ? "" : Number(digits));
          }}
          disabled={disabled}
          aria-label={ariaLabel}
          className="h-5 text-xs"
        />
      );
    }
    case "select": {
      const stringValue = typeof value === "string" ? value : "";
      return (
        <Select value={stringValue} onValueChange={onChange} disabled={disabled}>
          <SelectTrigger id={id} aria-label={ariaLabel} className="h-5 w-full text-xs">
            <SelectValue placeholder="Select" />
          </SelectTrigger>
          <SelectContent>
            {row.options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    case "phone": {
      const digits = typeof value === "string" ? value.replaceAll(/\D/gu, "").slice(0, PHONE_NUMBER_DIGITS) : "";
      return (
        <Input
          id={id}
          type="tel"
          inputMode="numeric"
          value={formatPhoneDisplay(digits)}
          onChange={(event) => {
            const formatted = applyDigitFormatWithCaret(event, formatPhoneFromRaw);
            onChange(formatted.replaceAll(/\D/gu, ""));
          }}
          onBlur={onBlur}
          disabled={disabled}
          maxLength={20}
          placeholder={row.placeholder}
          aria-label={ariaLabel}
          aria-invalid={invalid}
          className={cn("h-5 text-xs", invalid && "border-destructive")}
        />
      );
    }
    default: {
      return (
        <Input
          id={id}
          type="text"
          value={typeof value === "string" ? value : ""}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-invalid={invalid}
          placeholder={row.placeholder}
          className={cn("h-5 text-xs", invalid && "border-destructive")}
        />
      );
    }
  }
}

// ---------------------------------------------------------------------------
// CallScriptModal (OR-21767)
//
// Draggable, dismissable "Call Script" modal opened from an income block's
// Employment Verification Phone row. Renders a "Verified Information" /
// "Saved Information" comparison for every employment-verification field
// (Saved is read from the dashboard's Employment record for this person and
// income index), plus a side panel with Approval #, Driver name/SSN,
// verification notes, and prior call history (only fetched while open).
//
// The Dialog shell always renders (so Escape/outside-click/focus behavior is
// always present); the data-driven body only renders once both ids are known.
//
// Save vs. close, deliberately split (a call script session can involve
// several saves before the analyst is done with this income row): a
// successful save (see `handleConfirmSave`) refreshes THIS employment's
// history, the dashboard, and the stipulations list, and fires the
// pre-approval-advance recalc for pre-approval deals (see useSaveCallScript)
// — "Saved Information" is built from history (the dialed-phone row) plus
// the dashboard's Employment record (everything else, since the save
// actually persists changes there; the Signers form's own resync also
// reacts to that dashboard refresh — see useSignersForm) — and clears
// "Verified Information" back to blank for the next call. Closing the modal
// has nothing special to do as a result.
// ---------------------------------------------------------------------------
export function CallScriptModal({
  open,
  onOpenChange,
  approvalId,
  applicantId,
  personPath,
  index,
  control,
}: CallScriptModalProperties) {
  const { t } = useTranslation();
  const readOnly = useFormReadOnly();
  const { offset, onHeaderPointerDown } = useDraggableDialog(open);
  const hasIds = Boolean(approvalId && applicantId);
  const uid = useId();
  const queryClient = useQueryClient();
  const setShouldForceExit = useForceExitStore((state) => state.setShouldForceExit);

  const dashboard = useDscWorkbench(approvalId ?? "");
  const employment = dashboard.data?.[personPath]?.income?.employments?.[index];
  // BFF address-type id 1 = CURRENT (physical) — the applicant's primary
  // residence address, whose primary key the save contract expects.
  const residenceAddresses = dashboard.data?.[personPath]?.residence?.addresses;
  const primaryAddress =
    residenceAddresses?.find((address) => address.addressType === "PHYSICAL" || address.addressTypeId === 1) ??
    residenceAddresses?.[0];

  const driverFirstName = useWatch({ control, name: "driver.customerInfo.firstName" });
  const driverLastName = useWatch({ control, name: "driver.customerInfo.lastName" });
  const driverSsn = useWatch({ control, name: "driver.customerInfo.ssnOrItin" });
  const driverName = [driverFirstName, driverLastName].filter(Boolean).join(" ");
  const personLabel = t(personPath === "driver" ? "signers.driver" : "signers.secondSigner");

  const history = useCallScriptHistory(approvalId ?? "", applicantId ?? "", { enabled: open && hasIds });
  useEffect(() => {
    if (open && hasIds) {
      prefetchAllCallScriptQuestions(queryClient);
    }
  }, [open, hasIds, queryClient]);
  const saveCallScript = useSaveCallScript();
  const [note, setNote] = useState("");
  // A successful save can still carry non-fatal warnings (e.g. recap-service
  // couldn't write an invalid phone number back to Employment) — grouped by
  // `code` for display (see WARNING_CODE_LABEL_KEYS), cleared on open and
  // replaced (not appended) by each new save.
  const [saveWarnings, setSaveWarnings] = useState<CallScriptSaveWarning[]>([]);
  const [verified, setVerified] = useState<CallScriptVerifiedFields>(EMPTY_CALL_SCRIPT_VERIFIED_FIELDS);
  const [fieldStates, setFieldStates] = useState<CallScriptFieldStates>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [changedFieldRows, setChangedFieldRows] = useState<ChangedFieldRow[]>([]);
  const [confirmError, setConfirmError] = useState<string>();
  const [isCheckingForceExit, setIsCheckingForceExit] = useState(false);
  const [forceExitWarningOpen, setForceExitWarningOpen] = useState(false);
  // Fax Verification (spec §6.3) — a sibling flow triggered from the same
  // screen: save() runs immediately (no "are you sure" confirm, unlike the
  // main Save button), then a separate popup opens to fax a verification
  // letter. No BFF endpoint exists for actually sending the fax yet, so the
  // send action is a visible, disabled stub pending backend support.
  const [faxOpen, setFaxOpen] = useState(false);
  const [fax, setFax] = useState({ faxTo: "", faxCompanyName: "", faxNumber: "", faxAddtlNotes: "" });
  // Modal-open orchestration (OR-21767 spec §2): before the analyst can start
  // a call, any unsaved edits elsewhere on the form must be persisted first —
  // legacy CAPS2 saved the whole Income tab (and re-ran stipulations/advance
  // recalc) before ever opening the popup. `useSaveAllSections` is this app's
  // equivalent aggregate save + recalc orchestrator, so it's reused here
  // rather than duplicating that chain. While it runs, the comparison form
  // stays hidden behind a loading state so "Saved Information" is never built
  // from a stale dashboard snapshot.
  const [isPreparingModal, setIsPreparingModal] = useState(false);
  const [prepareError, setPrepareError] = useState<string>();
  const { saveAll } = useSaveAllSections();
  // Picking a date fires the DatePicker's onChange and onBlur in the same batch, so a
  // `verified` read from render-time closure in the onBlur handler below would still see
  // the pre-pick value. This ref is updated synchronously inside updateVerified (and on
  // reset), so the blur handler always validates against the value just picked/typed.
  const verifiedReference = useRef(verified);
  // Tracks the last ZIP the auto-lookup effect has already filled City/State
  // for, so it never re-fires (or fights a manual edit) for the same ZIP;
  // reset whenever "Verified Information" itself resets (on open, and after a
  // successful save).
  // eslint-disable-next-line unicorn/no-useless-undefined -- useRef's no-init overload still requires the arg here
  const lastAutofilledZipReference = useRef<string | undefined>(undefined);
  // Same tracking, for the Physical Work Location ZIP's own auto-lookup.
  // eslint-disable-next-line unicorn/no-useless-undefined -- useRef's no-init overload still requires the arg here
  const lastAutofilledWorkLocationZipReference = useRef<string | undefined>(undefined);
  // Tracks mount state for the deferred setTimeout in validateDateFieldOnBlur, so it
  // doesn't call setFieldStates after the modal (and its state) has been torn down.
  const isMountedReference = useRef(true);
  useEffect(
    () => () => {
      isMountedReference.current = false;
    },
    []
  );

  /**
   * Clears "Verified Information" back to blank — what the analyst captures
   * live on the call, never a copy of saved/previously-dialed data. Shared by
   * the on-open reset below and the post-save reset in `handleConfirmSave`.
   */
  function resetVerifiedInformation() {
    setVerified(EMPTY_CALL_SCRIPT_VERIFIED_FIELDS);
    verifiedReference.current = EMPTY_CALL_SCRIPT_VERIFIED_FIELDS;
    setFieldStates({});
    lastAutofilledZipReference.current = undefined;
    lastAutofilledWorkLocationZipReference.current = undefined;
  }

  useEffect(() => {
    if (open) {
      resetVerifiedInformation();
      setNote("");
      setSaveWarnings([]);
      setConfirmOpen(false);
      setChangedFieldRows([]);
      setConfirmError(undefined);
      setFaxOpen(false);
      setFax({ faxTo: "", faxCompanyName: "", faxNumber: "", faxAddtlNotes: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resetVerifiedInformation is stable per render; only `open` should retrigger this
  }, [open]);

  // Runs the save-income-first / recalc-then-open orchestration described
  // above. `retryToken` lets the error state's Retry button re-run it without
  // toggling `open` — a failed save (e.g. a transient network error)
  // shouldn't strand the analyst with no way back short of closing and
  // reopening the modal.
  const [retryToken, setRetryToken] = useState(0);
  useEffect(() => {
    // Read-only deals have nothing to save (the visibility gate already keeps
    // the button from being reached in this state, but guard defensively) —
    // skip straight to showing the read-only comparison view.
    if (!open || !hasIds || !approvalId || !applicantId || readOnly) {
      return;
    }
    let cancelled = false;
    setIsPreparingModal(true);
    setPrepareError(undefined);
    void (async () => {
      // Tracks whether the force-exit branch below fired, so the `finally`
      // can leave `isPreparingModal` true rather than momentarily revealing
      // the comparison form while the parent processes `onOpenChange(false)`.
      let forceExiting = false;
      try {
        const status = await queryClient.fetchQuery({ ...applicationStatusQuery(approvalId), staleTime: 0 });
        const isForceExit = computeShouldForceExit({ isPreApprovalVoid: status.isPreApprovalVoid, readOnly });
        setShouldForceExit(isForceExit);
        if (isForceExit) {
          forceExiting = true;
          if (!cancelled) {
            onOpenChange(false);
            setForceExitWarningOpen(true);
          }
          return;
        }

        const result = await saveAll(approvalId, isPreApprovalApp(status));
        if (cancelled) {
          return;
        }
        if (!result.ok && result.apiCallMade) {
          setPrepareError(t("review.income.callScriptModal.prepareSaveFailed"));
          return;
        }

        // saveAll already awaits the dashboard cache refresh internally
        // (see useSaveAllSections), so the "Saved Information" column below
        // is already built from the just-saved data by the time this runs.
      } catch (error) {
        if (!cancelled && !(error instanceof CancelledError)) {
          setPrepareError(getErrorMessage(error));
        }
      } finally {
        if (!cancelled && !forceExiting) {
          setIsPreparingModal(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- saveAll/queryClient/readOnly/onOpenChange are effectively stable per open+retry cycle; re-running on every render would refire the save
  }, [open, hasIds, approvalId, applicantId, readOnly, retryToken]);

  const canSave =
    hasIds && !readOnly && !saveCallScript.isPending && !isCheckingForceExit && !isPreparingModal && !prepareError;
  const rows = buildRows(t);
  const latestHistoryEntryForEmployment = filterHistoryEntriesForEmployment(
    history.data?.entries ?? [],
    employment?.id
  )[0];
  const savedFields: CallScriptVerifiedFields = {
    ...mapEmploymentToSavedFields(employment),
    phoneNumberDialed: latestHistoryEntryForEmployment?.phoneNumberDialed ?? "",
  };
  const noSavedValue = t("review.income.callScriptModal.noSavedValue");

  // TanStack Query v5 dropped per-query `onError`, so query failures are
  // toasted from an effect (mirrors PullPurchasedEmployerInfo).
  // eslint-disable-next-line unicorn/no-useless-undefined -- useRef's no-init overload still requires the arg here
  const toastedHistoryErrorReference = useRef<unknown>(undefined);
  useEffect(() => {
    if (history.isError && history.error !== toastedHistoryErrorReference.current) {
      toastedHistoryErrorReference.current = history.error;
      toast.error(t("review.income.callScriptModal.history.error", { message: getErrorMessage(history.error) }));
    } else if (!history.isError) {
      toastedHistoryErrorReference.current = undefined;
    }
  }, [history.isError, history.error, t]);

  function updateVerified<K extends keyof CallScriptVerifiedFields>(key: K, value: CallScriptVerifiedFields[K]) {
    setVerified((previous) => {
      const next = { ...previous, [key]: value };
      verifiedReference.current = next;
      return next;
    });
    // Clear any submit-time error styling on this field as soon as the analyst enters or
    // selects a new value — it only reappears on the next Save click (or, for the date
    // fields, on the next blur).
    setFieldStates((previous) => {
      const stateKey = key as keyof CallScriptFieldStates;
      if (previous[stateKey] === undefined) {
        return previous;
      }
      const next = { ...previous };
      delete next[stateKey];
      return next;
    });
  }

  // Employee Physical Address ZIP auto-lookup (spec §4): once the analyst
  // enters a complete 5-digit ZIP, look up city/state and fill them in.
  // Mirrors the Residence tab's useZipAutofill, but this modal keeps its own
  // plain state (not RHF), so the query is wired directly here instead of
  // reusing that hook. Only fires once per distinct ZIP value so it never
  // fights a manual City/State edit made after the autofill.
  const zipForLookup = isCompleteZip(verified.zip) ? verified.zip : undefined;
  const zipLookup = useZipLookup(zipForLookup);
  useEffect(() => {
    if (!zipForLookup || !zipLookup.data || lastAutofilledZipReference.current === zipForLookup) {
      return;
    }
    lastAutofilledZipReference.current = zipForLookup;
    if (zipLookup.data.city) {
      updateVerified("city", zipLookup.data.city);
    }
    if (zipLookup.data.state) {
      updateVerified("state", zipLookup.data.state);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- updateVerified is stable per render; only the lookup result should retrigger this
  }, [zipForLookup, zipLookup.data]);
  const zipLookupStatus: ZipLookupStatus = (() => {
    if (!zipForLookup) {
      return "idle";
    }
    if (zipLookup.isFetching) {
      return "loading";
    }
    if (zipLookup.isError) {
      return isHttpError(zipLookup.error, 404) ? "not-found" : "error";
    }
    return "idle";
  })();

  // Physical Work Location ZIP auto-lookup — same behavior as the Employee
  // Physical Address ZIP above, but fills workLocationCity/workLocationState.
  const workLocationZipForLookup = isCompleteZip(verified.workLocationZip) ? verified.workLocationZip : undefined;
  const workLocationZipLookup = useZipLookup(workLocationZipForLookup);
  useEffect(() => {
    if (
      !workLocationZipForLookup ||
      !workLocationZipLookup.data ||
      lastAutofilledWorkLocationZipReference.current === workLocationZipForLookup
    ) {
      return;
    }
    lastAutofilledWorkLocationZipReference.current = workLocationZipForLookup;
    if (workLocationZipLookup.data.city) {
      updateVerified("workLocationCity", workLocationZipLookup.data.city);
    }
    if (workLocationZipLookup.data.state) {
      updateVerified("workLocationState", workLocationZipLookup.data.state);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- updateVerified is stable per render; only the lookup result should retrigger this
  }, [workLocationZipForLookup, workLocationZipLookup.data]);
  const workLocationZipLookupStatus: ZipLookupStatus = (() => {
    if (!workLocationZipForLookup) {
      return "idle";
    }
    if (workLocationZipLookup.isFetching) {
      return "loading";
    }
    if (workLocationZipLookup.isError) {
      return isHttpError(workLocationZipLookup.error, 404) ? "not-found" : "error";
    }
    return "idle";
  })();

  // On-blur validation for the date fields (hireDate, dateOfTermination), mirroring
  // the Signers form's RHF `onBlur` mode: only the field just left is (re)checked,
  // rather than waiting for the submit-time pass to flag it.
  //
  // Deferred via setTimeout: the calendar overlay's day buttons are real, focusable
  // <button>s, so clicking one blurs the text input mid-click (before the picker's own
  // click handling — value commit + refocusing the input — finishes). Validating
  // synchronously in that blur re-renders the modal while the picker is still mid-click,
  // which in real browsers (not reproducible in jsdom) races the picker's own click
  // handling and drops the selection. Pushing the validation to a fresh task lets the
  // picker finish its click handling first.
  function validateDateFieldOnBlur(key: keyof CallScriptVerifiedFields) {
    setTimeout(() => {
      if (!isMountedReference.current) {
        return;
      }
      const validatedKey = key as keyof CallScriptFieldStates;
      const result = validateEmploymentVerification(verifiedReference.current, savedFields);
      if (result.fieldStates[validatedKey] === undefined) {
        return;
      }
      setFieldStates((previous) => ({ ...previous, [validatedKey]: result.fieldStates[validatedKey] }));
    }, 0);
  }

  // Submit-time-only validation (OR-21767): every field is checked
  // independently and just drives per-field error styling — no popup on
  // failure. Once every field passes, the "CAPS WARNING" confirm dialog opens
  // with a Verified-vs-Saved comparison of only the changed fields — unless
  // nothing actually changed, in which case the save proceeds immediately.
  const handleSaveClick = () => {
    if (!canSave || !approvalId || !applicantId) {
      return;
    }
    const result = validateEmploymentVerification(verified, savedFields);
    setFieldStates(result.fieldStates);
    if (!result.isValid) {
      return;
    }
    const changed = buildChangedFieldRows(rows, verified, savedFields, noSavedValue);
    if (changed.length === 0) {
      void handleConfirmSave();
      return;
    }
    setChangedFieldRows(changed);
    setConfirmOpen(true);
  };

  // Re-checks force-exit fresh (bypassing the query's staleTime) at the
  // moment the confirm dialog's OK is clicked, mirroring ReviewPage's
  // handleSave — the button-click-to-open guard's cached store value could be
  // stale by the time the analyst confirms the save. `openFaxOnSuccess` is
  // set by the Fax Verification flow (spec §6.3), which saves immediately
  // with no confirm dialog and then opens the fax popup on success.
  const handleConfirmSave = async (options?: { openFaxOnSuccess?: boolean }) => {
    if (!approvalId || !applicantId) {
      return;
    }
    setConfirmError(undefined);
    // Cleared here (not just on modal open) so a warning from an earlier
    // successful save doesn't linger alongside a hard error from this attempt.
    setSaveWarnings([]);
    setIsCheckingForceExit(true);
    try {
      const status = await queryClient.fetchQuery({ ...applicationStatusQuery(approvalId), staleTime: 0 });
      const isForceExit = computeShouldForceExit({ isPreApprovalVoid: status.isPreApprovalVoid, readOnly });
      setShouldForceExit(isForceExit);

      if (isForceExit) {
        setConfirmOpen(false);
        setForceExitWarningOpen(true);
        return;
      }

      // Awaited (not read from cache synchronously) so a Save that races
      // ahead of the modal-open prefetch still resolves every field's
      // verificationQuestionPk instead of silently posting an empty
      // verificationAnswers[] — see ensureCallScriptQuestionsForPerson.
      const questions = await ensureCallScriptQuestionsForPerson(queryClient, personPath);

      setConfirmOpen(false);
      saveCallScript.mutate(
        {
          approvalId,
          applicantId,
          personPath,
          incomeIndex: index,
          applicantEmploymentPk: employment?.id,
          applicantAddressPk: primaryAddress?.id,
          note: note.trim(),
          verifiedFields: verified,
          savedFieldsForAnswers: buildSavedFieldsForAnswers(
            employment,
            filterHistoryEntriesForEmployment(history.data?.entries ?? [], employment?.id)[0]
          ),
          questionPkMap: buildQuestionPkMap(questions),
          employerPhoneExtension: employment?.phoneExtension,
        },
        {
          onSuccess: (result) => {
            // `result` is only `undefined` when useSaveCallScript's mutationFn
            // no-op'd (a force-exit was detected between this handler's own
            // check above and the mutation actually running) — nothing was
            // saved, so don't wipe what the analyst just captured on the call.
            if (!result) {
              return;
            }
            setNote("");
            resetVerifiedInformation();
            setSaveWarnings(result.warnings ?? []);
            if (options?.openFaxOnSuccess) {
              setFaxOpen(true);
            }
          },
        }
      );
    } catch (error) {
      if (!(error instanceof CancelledError)) {
        setConfirmError(getErrorMessage(error));
      }
    } finally {
      setIsCheckingForceExit(false);
    }
  };

  // Fax Verification's own entry point (spec §6.3): runs the same field
  // validation as the main Save button, but on success calls save()
  // immediately — no "changed fields" confirm dialog — then opens the fax
  // popup.
  const handleFaxVerificationClick = () => {
    if (!canSave || !approvalId || !applicantId) {
      return;
    }
    const result = validateEmploymentVerification(verified, savedFields);
    setFieldStates(result.fieldStates);
    if (!result.isValid) {
      return;
    }
    void handleConfirmSave({ openFaxOnSuccess: true });
  };

  const entries = filterHistoryEntriesForEmployment(history.data?.entries ?? [], employment?.id);
  const historyEntryHeader = t("review.income.callScriptModal.history.entryHeader", {
    incomeNumber: index + 1,
    personLabel: personLabel.toUpperCase(),
  });
  const errorMessages: Partial<Record<keyof CallScriptVerifiedFields, string>> = {
    employerName: t("review.income.callScriptModal.errors.employerName"),
    phoneNumberDialed: t("review.income.callScriptModal.errors.phoneNumberDialed"),
    hireDate: t("review.income.callScriptModal.errors.hireDate"),
    zip: t("review.income.callScriptModal.errors.zip"),
    employerPhone: t("review.income.callScriptModal.errors.employerPhone"),
    workLocationPhone: t("review.income.callScriptModal.errors.workLocationPhone"),
    dateOfTermination: t("review.income.callScriptModal.errors.dateOfTermination"),
  };

  return (
    <>
      {/*
       * `modal={false}`: uikit's DatePicker portals its calendar into document.body
       * from its own bundled copy of Radix, which shares no layer/focus state with the
       * copy this Dialog uses. Under a modal Dialog that copy leaves the calendar with
       * the `pointer-events: none` Radix puts on <body>, so day cells cannot be clicked
       * at all, and the modal focus-trap fights the DatePicker's focus-to-open handler
       * into a render loop the moment a day does get selected. Dropping modal mode
       * removes both: the overlay still blocks the page and `onInteractOutside` still
       * refuses outside dismissal, so nothing about how this dialog behaves changes.
       */}
      <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
        <DialogContent
          showCloseButton={false}
          onInteractOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => {
            // Escape belongs to the calendar while one is open. The DatePicker's Radix
            // copy shares no layer stack with this Dialog's, so both would dismiss on
            // the same keypress — taking the whole call script, and everything typed
            // into it, down with the calendar.
            if (isCalendarOverlayOpen()) {
              event.preventDefault();
              closeCalendarOverlay();
            }
          }}
          style={{ translate: `calc(-50% + ${offset.x}px) calc(-50% + ${offset.y}px)` }}
          className="gap-0 overflow-hidden p-0 sm:max-w-3xl lg:max-w-5xl"
        >
          <div
            data-testid="call-script-header"
            onPointerDown={onHeaderPointerDown}
            className="bg-primary text-primary-foreground flex cursor-grab touch-none select-none items-center justify-between border-b px-4 py-2 active:cursor-grabbing"
          >
            <DialogTitle className="text-primary-foreground text-sm font-semibold">
              {t("review.income.callScriptModal.title", { incomeNumber: index + 1, personLabel })}
            </DialogTitle>
            <DialogDescription className="sr-only">{t("review.income.callScriptModal.description")}</DialogDescription>
            <DialogClose
              className="text-primary-foreground/80 hover:text-primary-foreground p-0.5 transition-colors"
              aria-label={t("review.income.callScriptModal.close")}
            >
              <X className="size-4.5" aria-hidden="true" />
            </DialogClose>
          </div>

          {hasIds && isPreparingModal && (
            <div
              className="flex flex-col gap-3 bg-white px-6 py-8"
              role="status"
              aria-label={t("review.income.callScriptModal.preparing")}
            >
              <Skeleton loading active paragraph={{ rows: 6 }} />
            </div>
          )}

          {hasIds && !isPreparingModal && prepareError && (
            <div className="flex flex-col gap-4 bg-white px-6 py-8">
              <p role="alert" className="text-destructive text-sm">
                {t("review.income.callScriptModal.prepareErrorPrefix", { message: prepareError })}
              </p>
              <div className="flex gap-2">
                <Button type="button" size="xs" onClick={() => setRetryToken((token) => token + 1)}>
                  {t("review.income.callScriptModal.retry")}
                </Button>
                <Button type="button" variant="outline" size="xs" onClick={() => onOpenChange(false)}>
                  {t("review.income.callScriptModal.exit")}
                </Button>
              </div>
            </div>
          )}

          {hasIds && !isPreparingModal && !prepareError && (
            <div className="flex flex-col">
              <div className="bg-white px-4">
                {saveCallScript.isError && (
                  <p role="alert" className="text-destructive mt-2 text-center text-xs">
                    {t("review.income.callScriptModal.saveError", {
                      message: getErrorMessage(saveCallScript.error),
                    })}
                  </p>
                )}
                {saveWarnings.length > 0 && (
                  <div role="alert" className="mt-2 flex flex-col items-center gap-2">
                    {groupWarningsByCode(saveWarnings).map((group) => (
                      <div key={group.code} className="text-center">
                        <p className="text-destructive text-xs font-semibold">
                          {t(WARNING_CODE_LABEL_KEYS[group.code] ?? GENERIC_WARNING_LABEL_KEY)}
                        </p>
                        <ul className="text-destructive list-inside list-disc text-xs">
                          {group.details.map((detail, detailIndex) => (
                            // Indexed, not keyed by `detail` text — two warnings in the
                            // same group can share identical detail text.
                            // eslint-disable-next-line react/no-array-index-key -- static list rebuilt wholesale per save; no reordering/insertion to key by
                            <li key={detailIndex}>{detail}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
                <div className="grid h-[75vh] grid-cols-1 gap-2 py-2 lg:grid-cols-[2fr_1fr]">
                  {/* Verified vs Saved comparison */}
                  <div className="grid min-h-0 grid-cols-1 overflow-y-auto text-[11px] sm:grid-cols-[auto_1fr_1fr]">
                    <div />
                    <div className="text-primary text-[11px] font-semibold">
                      {t("review.income.callScriptModal.verifiedInformation")}
                    </div>
                    <div className="text-primary text-[11px] font-semibold">
                      {t("review.income.callScriptModal.savedInformation")}
                    </div>
                    {rows
                      .filter((row) => !row.showWhen || row.showWhen(verified, savedFields))
                      .map((row) => {
                        const id = `${uid}-${row.key}`;
                        const hasSavedCounterpart = !NO_SAVED_COUNTERPART_FIELDS.has(row.key);
                        // Checkbox rows (currently just "Different Work Location") have no
                        // Saved-column comparison — the checkbox and its label live together
                        // in a single full-width row instead of the usual 3-column layout.
                        if (row.kind === "checkbox") {
                          return (
                            <div key={row.key} className="flex items-center gap-1.5 py-0 sm:col-span-2 sm:col-start-2">
                              <VerifiedControl
                                row={row}
                                id={id}
                                value={verified[row.key]}
                                onChange={(value) => updateVerified(row.key, value)}
                                disabled={readOnly || saveCallScript.isPending}
                                ariaLabel={row.label}
                              />
                              <label htmlFor={id} className="text-primary text-[11px] font-medium">
                                {row.label}
                              </label>
                            </div>
                          );
                        }
                        return (
                          <div key={row.key} className="contents [&>*]:py-0">
                            {/* Not a <label htmlFor>: clicking it must not focus/activate the Verified
                                input — VerifiedControl already gets its accessible name via aria-label. */}
                            <span className="text-primary self-center pr-1.5 text-right text-[11px] font-medium">
                              {row.label}
                            </span>
                            <div
                              className={cn(
                                "max-w-[90%] self-center",
                                isRowChanged(row, verified[row.key], savedFields[row.key]) && "rounded-md bg-amber-100"
                              )}
                              data-testid={`${row.key}-verified-cell`}
                            >
                              <VerifiedControl
                                row={row}
                                id={id}
                                value={verified[row.key]}
                                onChange={(value) => updateVerified(row.key, value)}
                                onBlur={row.kind === "date" ? () => validateDateFieldOnBlur(row.key) : undefined}
                                disabled={readOnly || saveCallScript.isPending}
                                ariaLabel={row.label}
                                invalid={fieldStates[row.key as keyof CallScriptFieldStates] === "error"}
                              />
                              {fieldStates[row.key as keyof CallScriptFieldStates] === "error" && (
                                <p role="alert" className="text-destructive mt-0.5 text-[11px]">
                                  {errorMessages[row.key]}
                                </p>
                              )}
                              {row.key === "zip" && <ZipLookupMessage status={zipLookupStatus} />}
                              {row.key === "workLocationZip" && (
                                <ZipLookupMessage status={workLocationZipLookupStatus} />
                              )}
                            </div>
                            <div className="max-w-[90%] self-center">
                              {hasSavedCounterpart ? (
                                <VerifiedControl
                                  row={row}
                                  id={`${id}-saved`}
                                  value={savedFields[row.key]}
                                  onChange={() => {}}
                                  disabled
                                  ariaLabel={t("review.income.callScriptModal.savedFieldLabel", { field: row.label })}
                                />
                              ) : (
                                <span className="text-muted-foreground">{noSavedValue}</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>

                  {/* Side panel: Approval / Driver / Notes / History */}
                  <div className="flex min-h-0 flex-col gap-2">
                    <div className="text-xs leading-tight">
                      <p>
                        <span className="text-primary font-semibold">
                          {t("review.income.callScriptModal.approvalNumber")}:
                        </span>{" "}
                        {approvalId}
                      </p>
                      <p>
                        <span className="text-primary font-semibold">{t("review.income.callScriptModal.driver")}:</span>{" "}
                        {driverName || noSavedValue}
                        {driverSsn && ` (${formatSsn(driverSsn)})`}
                      </p>
                    </div>

                    <div>
                      <label htmlFor="call-script-note" className="text-primary mb-1 block text-xs font-semibold">
                        {t("review.income.callScriptModal.notes.label")}
                      </label>
                      <Textarea
                        id="call-script-note"
                        value={note}
                        onChange={(event) => setNote(event.target.value)}
                        placeholder={t("review.income.callScriptModal.notes.placeholder")}
                        disabled={readOnly || saveCallScript.isPending}
                        className="min-h-16 resize-none"
                      />
                    </div>

                    <div className="flex min-h-0 flex-1 flex-col">
                      <h3 className="text-primary mb-2 text-xs font-semibold">
                        {t("review.income.callScriptModal.history.title")}
                      </h3>
                      {history.isLoading ? (
                        <div
                          role="status"
                          aria-label={t("review.income.callScriptModal.history.loading")}
                          className="px-1 py-3"
                        >
                          <Skeleton loading active paragraph={{ rows: 3 }} />
                        </div>
                      ) : entries.length === 0 ? (
                        <p className="text-muted-foreground text-xs">
                          {t("review.income.callScriptModal.history.empty")}
                        </p>
                      ) : (
                        <div className="bg-muted min-h-0 flex-1 overflow-y-auto rounded-lg border px-2 py-1.5">
                          {entries.map((entry, entryIndex) => (
                            <div
                              key={entry.verificationPhoneCallPk}
                              className={cn("text-[10px]", entryIndex > 0 && "border-border mt-2 border-t pt-2")}
                            >
                              <div className="text-foreground font-semibold">
                                {formatNoteDatetime(entry.occurredAt, { includeSeconds: true })}-{entry.analystName}
                              </div>
                              <div className="text-muted-foreground">{historyEntryHeader}</div>
                              {entry.answers.map((answer) => (
                                <div key={answer.verificationAnswerPk}>
                                  {answer.questionText} - {formatHistoryAnswerValue(answer)}
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <DialogFooter className="bg-muted border-t px-4 py-2 sm:flex-row">
                <Button type="button" size="xs" disabled={!canSave} onClick={handleSaveClick}>
                  {t("review.income.callScriptModal.save")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  disabled={!canSave}
                  onClick={handleFaxVerificationClick}
                >
                  {t("review.income.callScriptModal.faxVerification.open")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  disabled={saveCallScript.isPending}
                  onClick={() => onOpenChange(false)}
                >
                  {t("review.income.callScriptModal.exit")}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={faxOpen} onOpenChange={setFaxOpen}>
        <DialogContent className="sm:max-w-md">
          <div className="space-y-4">
            <div>
              <DialogTitle className="text-lg font-semibold">
                {t("review.income.callScriptModal.faxVerification.title")}
              </DialogTitle>
              <DialogDescription className="text-muted-foreground text-sm">
                {t("review.income.callScriptModal.faxVerification.description")}
              </DialogDescription>
            </div>
            <div className="space-y-3">
              <div className="space-y-1">
                <label htmlFor={`${uid}-fax-to`} className="text-xs font-medium">
                  {t("review.income.callScriptModal.faxVerification.faxTo")}
                </label>
                <Input
                  id={`${uid}-fax-to`}
                  value={fax.faxTo}
                  onChange={(event) => setFax((previous) => ({ ...previous, faxTo: event.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <label htmlFor={`${uid}-fax-company-name`} className="text-xs font-medium">
                  {t("review.income.callScriptModal.faxVerification.faxCompanyName")}
                </label>
                <Input
                  id={`${uid}-fax-company-name`}
                  value={fax.faxCompanyName}
                  onChange={(event) => setFax((previous) => ({ ...previous, faxCompanyName: event.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <label htmlFor={`${uid}-fax-number`} className="text-xs font-medium">
                  {t("review.income.callScriptModal.faxVerification.faxNumber")}
                </label>
                <Input
                  id={`${uid}-fax-number`}
                  value={fax.faxNumber}
                  onChange={(event) => setFax((previous) => ({ ...previous, faxNumber: event.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <label htmlFor={`${uid}-fax-notes`} className="text-xs font-medium">
                  {t("review.income.callScriptModal.faxVerification.faxAddtlNotes")}
                </label>
                <Textarea
                  id={`${uid}-fax-notes`}
                  value={fax.faxAddtlNotes}
                  onChange={(event) => setFax((previous) => ({ ...previous, faxAddtlNotes: event.target.value }))}
                />
              </div>
            </div>
            <p className="text-muted-foreground text-xs">
              {t("review.income.callScriptModal.faxVerification.pendingBackendSupport")}
            </p>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" size="xs" onClick={() => setFaxOpen(false)}>
                {t("review.income.callScriptModal.exit")}
              </Button>
              <Button
                type="button"
                size="xs"
                disabled
                title={t("review.income.callScriptModal.faxVerification.pendingBackendSupport")}
              >
                {t("review.income.callScriptModal.faxVerification.send")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-lg">
          <div className="gap-4 space-y-4">
            <div>
              <DialogTitle className="text-lg font-semibold">
                {t("review.income.callScriptModal.confirmSave.title")}
              </DialogTitle>
              <DialogDescription className="sr-only">
                {t("review.income.callScriptModal.confirmSave.title")}
              </DialogDescription>
            </div>
            <p className="text-sm">{t("review.income.callScriptModal.confirmSave.message")}</p>
            <p className="text-sm">{t("review.income.callScriptModal.confirmSave.question")}</p>
            {changedFieldRows.length > 0 && (
              <div className="border-border max-h-64 overflow-y-auto rounded-lg border">
                <div className="grid grid-cols-[1fr_1fr_1fr] gap-x-2 gap-y-1 p-2 text-xs">
                  <div />
                  <div className="text-primary font-semibold">
                    {t("review.income.callScriptModal.verifiedInformation")}
                  </div>
                  <div className="text-primary font-semibold">
                    {t("review.income.callScriptModal.savedInformation")}
                  </div>
                  {changedFieldRows.map((row) => (
                    <div key={row.key} className="contents">
                      <div className="text-primary font-medium">{row.label}</div>
                      <div className="break-words">{row.verifiedDisplay}</div>
                      <div className="text-muted-foreground break-words">{row.savedDisplay}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {confirmError && (
              <p role="alert" className="text-destructive text-xs">
                {confirmError}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="xs"
                disabled={isCheckingForceExit}
                onClick={() => setConfirmOpen(false)}
              >
                {t("review.income.callScriptModal.confirmSave.cancel")}
              </Button>
              <Button type="button" size="xs" disabled={isCheckingForceExit} onClick={() => void handleConfirmSave()}>
                {t("review.income.callScriptModal.confirmSave.ok")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ForceExitWarningModal
        open={forceExitWarningOpen}
        onOpenChange={setForceExitWarningOpen}
        onConfirm={() => setForceExitWarningOpen(false)}
      />
    </>
  );
}
